import { messstelle, stoerung, messwertLog, gespeicherteSuche, anhang, messgroesse, recorder } from './../../utils/apiTypes';
import { stoerungsmanagementState } from './stoerungsmanagementTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../../app/store';
import { deleteWithAuth, getWithAuth, postJsonWithAuth, postWithAuth, putToS3, putWithAuth } from '../../utils/api';
import { openSnackbar } from '../../features/snackbar/globalSnackbarSlice';
import { isValidDate, logError, nanosecondsTimeStampToDate } from '../../utils/utils';
import { addTabNotResetDate, loadGespeicherteSuche, onDropDownSelect, updateField } from '../netzwirtschaft/netzwirtschaftSlice';
import { ExportToExcel } from '../../features/excelExport/ExportToExcel';
import { netzwirtschaftFieldsData } from '../netzwirtschaft/netzwirtschaftTypes';
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../utils/errorHandling';

const currentTime = new Date();
currentTime.setUTCSeconds(0, 0);

export const stoerungDummy: stoerung = {
    id: 0,
    name: "",
    messstelle: "",
    start: currentTime,
    ende: currentTime,
    kommentar: "",
    stoerschriebe: [],
    graphen: [],
    anhaenge: [],
}

const initialState: stoerungsmanagementState = {
    stoerungsliste: [],
    aktuelleStoerung: stoerungDummy,
    alteStoerung: stoerungDummy,
    editEntry: false,
    newEntry: false,
    editDirty: 0,
    viewMode: false,
    error: [],
    messstellen: [],
    freischaltungen: [],
    stoerungslisteLoading: false,
    stoerschrieblisteLoading: false,
    graphenlisteLoading: false,
    anhanglisteLoading: false,
    messstellenLoading: false,
    exportLoading: -1,
    gespeicherteSuchen: [],
    sorting: [
        {
            field: 'name',
            sort: 'asc',
        },
    ]
}

const stoerungsmanagementSlice = createSlice({
    name: "stoerungsmanagement",
    initialState,
    reducers: {
        setMessstellen: (state, action: PayloadAction<Array<messstelle>>) => {
            state.messstellen = action.payload;
        },
        updateStoerungen: (state, action: PayloadAction<stoerung>) => {
            const updateIndex = state.stoerungsliste.findIndex(
                (x: stoerung) => x.id == action.payload.id
            );
            const firstPart = state.stoerungsliste.slice(0, updateIndex);
            let lastPart = Array<stoerung>();
            if (updateIndex + 1 < state.stoerungsliste.length)
                lastPart = state.stoerungsliste.slice(updateIndex + 1);
            state.stoerungsliste = [...firstPart, action.payload, ...lastPart];
        },

        addToStoerungen: (state, action: PayloadAction<stoerung>) => {
            const stoerungen = state.stoerungsliste;
            stoerungen.push(action.payload);
            state.stoerungsliste = [...stoerungen].sort((a, b) => ((new Date(b.start)).getTime() - (new Date(a.start)).getTime()));
        },
        removeFromStoerungen: (state, action: PayloadAction<stoerung>) => {
            const updateIndex = state.stoerungsliste.findIndex(
                (x) => x.id == action.payload.id
            );
            const firstPart = state.stoerungsliste.slice(0, updateIndex);
            let lastPart = Array<stoerung>();
            if (updateIndex + 1 < state.stoerungsliste.length)
                lastPart = state.stoerungsliste.slice(updateIndex + 1);
            state.stoerungsliste = [...firstPart, ...lastPart];
        },
        setStoerungen: (state, action: PayloadAction<Array<stoerung>>) => {
            state.stoerungsliste = action.payload;
        },
        setAlteStoerung: (state, action: PayloadAction<stoerung>) => {
            state.alteStoerung = action.payload;
        },
        setAktuelleStoerung: (state, action: PayloadAction<stoerung>) => {
            state.aktuelleStoerung = action.payload;
            state.alteStoerung = action.payload;
        },
        resetAktuelleStoerung: (state) => {
            state.aktuelleStoerung = state.alteStoerung;
        },
        setEditEntry: (state) => {
            state.editEntry = true;
        },
        resetEditEntry: (state) => {
            state.editEntry = false;
        },
        setNewEntry: (state) => {
            state.newEntry = true;
        },
        resetNewEntry: (state) => {
            state.newEntry = false;
        },
        resetEditDirty: (state) => {
            state.editDirty = 0;
        },
        setViewMode: (state) => {
            state.viewMode = true;
        },
        resetViewMode: (state) => {
            state.viewMode = false;
        },
        setError: (state, action: PayloadAction<string>) => {
            if (!state.error.includes(action.payload)) {
                state.error.push(action.payload);
            }
        },
        resetError: (state, action: PayloadAction<string>) => {
            if (state.error.includes(action.payload)) {
                state.error.splice(state.error.indexOf(action.payload), 1)
            }
        },
        setName: (state, action: PayloadAction<string>) => {
            state.aktuelleStoerung.name = action.payload;
            state.editDirty =
                state.aktuelleStoerung.name == state.alteStoerung.name
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setMessstelle: (state, action: PayloadAction<string>) => {
            state.aktuelleStoerung.messstelle = action.payload;
            state.editDirty =
                state.aktuelleStoerung.messstelle == state.alteStoerung.messstelle
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setKommentar: (state, action: PayloadAction<string>) => {
            state.aktuelleStoerung.kommentar = action.payload;
            state.editDirty =
                state.aktuelleStoerung.kommentar == state.alteStoerung.kommentar
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setStartDatum: (state, action: PayloadAction<Date | null>) => {
            if (action.payload != null) {
                state.aktuelleStoerung.start = new Date(action.payload);
                if (isValidDate(action.payload)) {
                    const endzeit = state.aktuelleStoerung.ende;
                    if (endzeit < state.aktuelleStoerung.start) {
                        state.aktuelleStoerung.ende = state.aktuelleStoerung.start;
                    }
                    state.editDirty =
                        state.aktuelleStoerung.start ==
                            state.alteStoerung.start
                            ? state.editDirty - 1
                            : state.editDirty + 1;
                    if (state.error.includes("Startdatum")) {
                        state.error.splice(state.error.indexOf("Startdatum"), 1);
                    }
                } else {
                    if (!state.error.includes("Startdatum")) {
                        state.error.push("Startdatum");
                    }
                }
            }
        },
        setEndDatum: (state, action: PayloadAction<Date | null>) => {
            if (action.payload != null) {
                state.aktuelleStoerung.ende = new Date(action.payload);
                if (isValidDate(action.payload)) {
                    state.editDirty =
                        state.aktuelleStoerung.ende ==
                            state.alteStoerung.ende
                            ? state.editDirty - 1
                            : state.editDirty + 1;
                    if (state.error.includes("Enddatum")) {
                        state.error.splice(state.error.indexOf("Enddatum"), 1);
                    }
                } else {
                    if (!state.error.includes("Enddatum")) {
                        state.error.push("Enddatum");
                    }
                }
            }
        },
        setStoerungslisteLoading: (state, action: PayloadAction<boolean>) => {
            state.stoerungslisteLoading = action.payload;
        },
        setStoerungschrieblisteLoading: (state, action: PayloadAction<boolean>) => {
            state.stoerschrieblisteLoading = action.payload;
        },
        setGraphenlisteLoading: (state, action: PayloadAction<boolean>) => {
            state.graphenlisteLoading = action.payload;
        },
        setAnhanglisteLoading: (state, action: PayloadAction<boolean>) => {
            state.anhanglisteLoading = action.payload;
        },
        setMessstellenLoading: (state, action: PayloadAction<boolean>) => {
            state.messstellenLoading = action.payload;
        },
        setExportLoading: (state, action: PayloadAction<number>) => {
            state.exportLoading = action.payload;
        },
        addStoerschriebeToAktuellerStoerung: (state, action: PayloadAction<Array<messwertLog>>) => {
            state.aktuelleStoerung.stoerschriebe = state.aktuelleStoerung.stoerschriebe.concat(action.payload);
            const index: number = state.stoerungsliste.findIndex(x => x.id == state.aktuelleStoerung.id);
            //eslint-disable-next-line security/detect-object-injection
            if (index > -1) state.stoerungsliste[index].stoerschriebe = state.stoerungsliste[index].stoerschriebe.concat(action.payload);
        },
        clearStoerschrieblisteFromAktuellerStoerung: (state) => {
            state.aktuelleStoerung.stoerschriebe = [];
            state.alteStoerung.stoerschriebe = [];
            const index: number = state.stoerungsliste.findIndex(x => x.id == state.aktuelleStoerung.id);
            //eslint-disable-next-line security/detect-object-injection
            if (index > -1) state.stoerungsliste[index].stoerschriebe = [];
        },
        setGespeicherteSuchen: (state, action: PayloadAction<Array<gespeicherteSuche>>) => {
            state.gespeicherteSuchen = action.payload;
        },
        setGraphenForAktuelleStoerung: (state, action: PayloadAction<Array<number>>) => {
            state.aktuelleStoerung.graphen = action.payload;
            state.alteStoerung.graphen = action.payload;
            const index: number = state.stoerungsliste.findIndex(x => x.id == state.aktuelleStoerung.id);
            //eslint-disable-next-line security/detect-object-injection
            if (index > -1) state.stoerungsliste[index].graphen = action.payload;
        },
        addGraphToAktuellerStoerung: (state, action: PayloadAction<number>) => {
            state.aktuelleStoerung.graphen.push(action.payload);
            state.editDirty =
                state.aktuelleStoerung.graphen == state.alteStoerung.graphen
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        removeGraphFromAktuellerStoerung: (state, action: PayloadAction<number>) => {
            state.aktuelleStoerung.graphen = state.aktuelleStoerung.graphen.filter(x => x != action.payload);
            state.editDirty =
                state.aktuelleStoerung.graphen == state.alteStoerung.graphen
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setAnhaengeForAktuelleStoerung: (state, action: PayloadAction<Array<anhang>>) => {
            state.aktuelleStoerung.anhaenge = action.payload;
            state.alteStoerung.anhaenge = action.payload;
            const index: number = state.stoerungsliste.findIndex(x => x.id == state.aktuelleStoerung.id);
            //eslint-disable-next-line security/detect-object-injection
            if (index > -1) state.stoerungsliste[index].anhaenge = action.payload;
            state.editDirty =
                state.aktuelleStoerung.anhaenge == state.alteStoerung.anhaenge
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        removeAnhangFromAktuellerStoerung: (state, action: PayloadAction<anhang>) => {
            state.aktuelleStoerung.anhaenge = state.aktuelleStoerung.anhaenge.filter(x => x.id != action.payload.id);
            state.editDirty =
                state.aktuelleStoerung.anhaenge == state.alteStoerung.anhaenge
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setSorting: (state, action: PayloadAction<GridSortModel>) => {
            state.sorting = action.payload;
        },
    }
});

export const {
    setAktuelleStoerung,
    setEditEntry,
    resetEditDirty,
    resetError,
    resetEditEntry,
    resetNewEntry,
    resetViewMode,
    setError,
    setNewEntry,
    setViewMode,
    addToStoerungen,
    removeFromStoerungen,
    resetAktuelleStoerung,
    setAlteStoerung,
    updateStoerungen,
    setEndDatum,
    setKommentar,
    setStartDatum,
    setMessstelle,
    setMessstellen,
    setStoerungen,
    setName,
    setStoerungslisteLoading,
    setStoerungschrieblisteLoading,
    setGraphenlisteLoading,
    setAnhanglisteLoading,
    addStoerschriebeToAktuellerStoerung,
    clearStoerschrieblisteFromAktuellerStoerung,
    setGespeicherteSuchen,
    setGraphenForAktuelleStoerung,
    addGraphToAktuellerStoerung,
    removeGraphFromAktuellerStoerung,
    setAnhaengeForAktuelleStoerung,
    removeAnhangFromAktuellerStoerung,
    setMessstellenLoading,
    setExportLoading,
    setSorting
} = stoerungsmanagementSlice.actions

export const selectAktuelleStoerung = (state: RootState): stoerung => state.stoerungsmanagement.aktuelleStoerung;
export const selectEditEntry = (state: RootState): boolean => state.stoerungsmanagement.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.stoerungsmanagement.newEntry;
export const selectEditDirty = (state: RootState): number => state.stoerungsmanagement.editDirty;
export const selectViewMode = (state: RootState): boolean => state.stoerungsmanagement.viewMode;
export const selectError = (state: RootState): boolean => state.stoerungsmanagement.error.length > 0;
export const selectMessstellen = (state: RootState): Array<messstelle> => state.stoerungsmanagement.messstellen;
export const selectStoerungsliste = (state: RootState): Array<stoerung> => state.stoerungsmanagement.stoerungsliste;
export const selectStoerungslisteLoading = (state: RootState): boolean => state.stoerungsmanagement.stoerungslisteLoading;
export const selectStoerschrieblisteLoading = (state: RootState): boolean => state.stoerungsmanagement.stoerschrieblisteLoading;
export const selectGraphenlisteLoading = (state: RootState): boolean => state.stoerungsmanagement.graphenlisteLoading;
export const selectAnhanglisteLoading = (state: RootState): boolean => state.stoerungsmanagement.anhanglisteLoading;
export const selectMesssstellenLoading = (state: RootState): boolean => state.stoerungsmanagement.messstellenLoading;
export const selectExportLoading = (state: RootState): number => state.stoerungsmanagement.exportLoading;
export const selectGespeicherteSuchen = (state: RootState): Array<gespeicherteSuche> => state.stoerungsmanagement.gespeicherteSuchen;
export const selectSorting = (state: RootState): GridSortModel => state.stoerungsmanagement.sorting;


export const getStoerungen = (idString: string | undefined) => (dispatch: AppDispatch): void => {
    dispatch(setStoerungslisteLoading(true));
    getWithAuth("STOERUNG", {})
        .then((response: Array<stoerung>) => {
            const stoerungen: Array<stoerung> = response.map(x => Object.assign({}, x, { graphen: [], stoerschriebe: [], anhaenge: [] }));
            dispatch(setStoerungen(stoerungen));
            if (idString) {
                const id = Number(idString);
                const aktuelleStoerung = stoerungen.find(x => x.id == id);
                if (aktuelleStoerung) dispatch(updateAktuelleStoerung(aktuelleStoerung));
            }
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setStoerungslisteLoading(false)));
}

export const getMessstellen = () => (dispatch: AppDispatch): void => {
    dispatch(setMessstellenLoading(true));
    getWithAuth("MESSSTELLE", {})
        .then((response) => {
            dispatch(setMessstellen(response));
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
    dispatch(setMessstellenLoading(false));
};

export const getStoerschriebe = (messstelle: string, start: Date, ende: Date) => (dispatch: AppDispatch): void => {
    dispatch(setStoerungschrieblisteLoading(true));
    dispatch(clearStoerschrieblisteFromAktuellerStoerung());
    const promisecrecb = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "crecb", from: start, to: ende, }, messstelle);
    const promisecreca = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "creca", from: start, to: ende, }, messstelle);
    Promise.all([promisecreca, promisecrecb]).then(([stoerschriebecreca, stoerschriebecrecb]: [Array<messwertLog>, Array<messwertLog>]) => {
        dispatch(addStoerschriebeToAktuellerStoerung(stoerschriebecreca));
        dispatch(addStoerschriebeToAktuellerStoerung(stoerschriebecrecb));
    }).catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
    }).finally(() => dispatch(setStoerungschrieblisteLoading(false)));
}

export const getGespeicherteSuchen = () => (dispatch: AppDispatch): void => {
    getWithAuth("GESPEICHERTESUCHEN", {})
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: Array<any>) => {
            const suchen: gespeicherteSuche[] = response.map(x => ({ id: x.id, name: x.name, wert: JSON.parse(x.wert), istLoeschbar: x.istLoeschbar }));
            dispatch(setGespeicherteSuchen(suchen));
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
};

export const getGraphenzuordnung = (stoerungsId: number) => (dispatch: AppDispatch): void => {
    getWithAuth("STOERUNGGRAPHENZUORDNUNGID", {}, stoerungsId.toString())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((graphen: Array<any>) => dispatch(setGraphenForAktuelleStoerung(graphen.map(x => x.konfig))))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
}

export const getAnhaenge = (stoerungsId: number) => (dispatch: AppDispatch): void => {
    getWithAuth("STOERUNGIDANHANG", {}, stoerungsId.toString())
        .then((anhaenge: Array<anhang>) => dispatch(setAnhaengeForAktuelleStoerung(anhaenge)))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
}

export const loadGraphen = (stoerungsId: number, gespeicherteSuchen: Array<gespeicherteSuche>) => (dispatch: AppDispatch): void => {
    dispatch(
        openSnackbar({
            message: `Die gewünschten Tabs werden sich in wenigen Sekunden öffnen.`,
            severity: "info",
            duration: 6000,
        })
    );
    getWithAuth("STOERUNGGRAPHENZUORDNUNGID", {}, stoerungsId.toString())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((graphen: Array<any>) => {
            if (graphen.length == 0) {
                dispatch(
                    openSnackbar({
                        message: `Dieser Störung sind keine Graphen zugeordnet.`,
                        severity: "info",
                        duration: 6000,
                    })
                );
            } else {
                gespeicherteSuchen.filter(x => graphen.findIndex(y => y.konfig == x.id) > -1).forEach((item) => {
                    dispatch(loadGespeicherteSuche(item.wert));
                });
            }
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        });
}

export const loadStoerschriebe = (uw: string, messstelle: string, start: Date, ende: Date) => (dispatch: AppDispatch): void => {
    dispatch(
        openSnackbar({
            message: `Die gewünschten Tabs werden sich in wenigen Sekunden öffnen.`,
            severity: "info",
            duration: 6000,
        })
    );
    const promisecrecb = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "crecb", from: start.toISOString(), to: ende.toISOString(), }, messstelle);
    const promisecreca = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "creca", from: start.toISOString(), to: ende.toISOString(), }, messstelle);
    Promise.all([promisecreca, promisecrecb]).then(([stoerschriebecreca, stoerschriebecrecb]: [Array<messwertLog>, Array<messwertLog>]) => {
        if (stoerschriebecreca.length == 0 && stoerschriebecrecb.length == 0) {
            dispatch(
                openSnackbar({
                    message: `Dieser Störung sind keine Störschriebe zugeordnet.`,
                    severity: "info",
                    duration: 6000,
                })
            );
        } else {
            stoerschriebecreca.concat(stoerschriebecrecb).forEach(item => {
                dispatch(addTabNotResetDate(messstelle + " " + item.datenklasse));
                dispatch(onDropDownSelect({
                    field: "uw",
                    value: uw
                }));
                dispatch(onDropDownSelect({
                    field: "messpunkt",
                    value: item.messstelle
                }));
                dispatch(updateField({
                    field: "startDatum",
                    value: nanosecondsTimeStampToDate(item.start)
                })
                );
                dispatch(updateField({
                    field: "endDatum",
                    value: nanosecondsTimeStampToDate(item.ende)
                })
                );

                switch (item.datenklasse) {
                    case "Oszilloskop (Störschriebe)":
                        dispatch(
                            onDropDownSelect({
                                field: "rekorder",
                                value: {
                                    klasse: "creca",
                                    kategorie: "Spannungen",
                                    hersteller: "Eberle",
                                    beschreibung: "Oszilloskop (Störschriebe)",
                                    messstelle: messstelle
                                }
                            })
                        );
                        dispatch(updateField({
                            field: "messgroesse",
                            value: [
                                {
                                    beschreibung: 'u0E / uNE',
                                    feldname: 'oscu0',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u1E / u1N',
                                    feldname: 'oscu1',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u2E / u2N',
                                    feldname: 'oscu2',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u3E / u3N',
                                    feldname: 'oscu3',
                                    einheit: 'V',
                                    unterkategorie: null,
                                }
                            ]
                        }));
                        break;
                    default:
                        dispatch(
                            onDropDownSelect({
                                field: "rekorder",
                                value: {
                                    klasse: "crecb",
                                    kategorie: "Spannungen",
                                    hersteller: "Eberle",
                                    beschreibung: "Effektivwerte (Störschriebe)",
                                    messstelle: messstelle
                                }
                            })
                        );
                        dispatch(updateField({
                            field: "messgroesse",
                            value: [
                                {
                                    beschreibung: 'u0E / uNE',
                                    feldname: 'halfcycleu0',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u1E / u1N',
                                    feldname: 'halfcycleu1',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u2E / u2N',
                                    feldname: 'halfcycleu2',
                                    einheit: 'V',
                                    unterkategorie: null,
                                },
                                {
                                    beschreibung: 'u3E / u3N',
                                    feldname: 'halfcycleu3',
                                    einheit: 'V',
                                    unterkategorie: null,
                                }
                            ]
                        }));
                }
            });
        }
    }).catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
    });
}

export const updateAktuelleStoerung = (stoerung: stoerung): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setAktuelleStoerung(stoerung));
    dispatch(getStoerschriebe(stoerung.messstelle, stoerung.start, stoerung.ende));
    dispatch(getGraphenzuordnung(stoerung.id));
    dispatch(getAnhaenge(stoerung.id));
    dispatch(setViewMode());
};

export const updateStoerung = (stoerung: stoerung): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setStoerungslisteLoading(true));
    const promiseStoerung = putWithAuth("STOERUNGID", {}, stoerung, stoerung.id.toString());
    const promiseGraphenzuordnung = putWithAuth("STOERUNGGRAPHENZUORDNUNGID", {}, stoerung.graphen, stoerung.id.toString());
    Promise.all([promiseStoerung, promiseGraphenzuordnung]).then(() => {
        dispatch(
            openSnackbar({
                message: `Störung '${stoerung.name}' wurde erfolgreich geändert.`,
                severity: "success",
                duration: 6000,
            })
        );
        dispatch(setAlteStoerung(stoerung));
        dispatch(updateStoerungen(stoerung));
        dispatch(resetEditEntry());
        dispatch(resetNewEntry());
        dispatch(resetEditDirty());
    })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setStoerungslisteLoading(false)));
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
    dispatch(resetAktuelleStoerung());
    dispatch(resetEditEntry());
    dispatch(resetNewEntry());
    dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
    dispatch(resetAktuelleStoerung());
    dispatch(resetEditEntry());
    dispatch(resetNewEntry());
    dispatch(resetEditDirty());
    dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
    dispatch(setNewEntry());
    dispatch(setAktuelleStoerung(stoerungDummy));
    dispatch(setEditEntry());
    dispatch(setViewMode());
};

export const createStoerung = (stoerung: stoerung): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setStoerungslisteLoading(true));
    dispatch(setStoerungslisteLoading(true));
    postJsonWithAuth("STOERUNG", {}, {
        id: stoerung.id,
        name: stoerung.name,
        messstelle: stoerung.messstelle,
        start: stoerung.start,
        ende: stoerung.ende,
        kommentar: stoerung.kommentar
    })
        .then((responseId: number) => {
            const stoerungRespone: stoerung = {
                id: responseId,
                name: stoerung.name,
                messstelle: stoerung.messstelle,
                start: stoerung.start,
                ende: stoerung.ende,
                kommentar: stoerung.kommentar,
                stoerschriebe: stoerung.stoerschriebe,
                graphen: stoerung.graphen,
                anhaenge: stoerung.anhaenge,
            }
            if (stoerung.graphen && stoerung.graphen.length > 0) {
                postWithAuth("STOERUNGIDANHANG", { fileName: stoerung.name }, {}, responseId.toString())
                    .then(() => {
                        dispatch(
                            openSnackbar({
                                message: `Störung '${stoerung.name}' wurde erfolgreich angelegt.`,
                                severity: "success",
                                duration: 6000,
                            }));
                        dispatch(setAlteStoerung(stoerungRespone));
                        dispatch(setAktuelleStoerung(stoerungDummy));
                        dispatch(addToStoerungen(stoerungRespone));
                        dispatch(resetEditEntry());
                        dispatch(resetNewEntry());
                        dispatch(resetEditDirty());
                        dispatch(resetViewMode());
                    })
                    .catch((error) => {
                        logError(error);
                        errorHandler(error, dispatch);
                    }).finally(() => dispatch(setStoerungslisteLoading(false)));
            } else {
                dispatch(
                    openSnackbar({
                        message: `Störung '${stoerung.name}' wurde erfolgreich angelegt.`,
                        severity: "success",
                        duration: 6000,
                    }));
                dispatch(setAlteStoerung(stoerungRespone));
                dispatch(setAktuelleStoerung(stoerungDummy));
                dispatch(addToStoerungen(stoerungRespone));
                dispatch(resetEditEntry());
                dispatch(resetNewEntry());
                dispatch(resetEditDirty());
                dispatch(resetViewMode());
            }
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => (stoerung.graphen && stoerung.graphen.length > 0) ? null : dispatch(setStoerungslisteLoading(false)));

};

export const deleteStoerung = (stoerung: stoerung): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setStoerungslisteLoading(true));
    deleteWithAuth("STOERUNGID", {}, stoerung.id.toString())
        .then(() => dispatch(setAktuelleStoerung(stoerungDummy)))
        .then(() => dispatch(removeFromStoerungen(stoerung)))
        .then(() => dispatch(resetEditEntry()))
        .then(() => dispatch(resetEditDirty()))
        .then(() => dispatch(resetViewMode()))
        .then(() => dispatch(
            openSnackbar({
                message: `Störung '${stoerung.name}' wurde erfolgreich gelöscht.`,
                severity: "success",
                duration: 6000,
            })
        ))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setStoerungslisteLoading(false)));
};

export const addAnhang = (stoerung: number, datei: File): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setAnhanglisteLoading(true));
    const data = new FormData();
    data.append('name', datei.name);
    data.append('type', datei.type);
    data.append('file', datei);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    postWithAuth("STOERUNGIDANHANG", { fileName: datei.name }, {}, stoerung.toString()).then((response: any) => {
        putToS3(response.url, datei.type, {}, datei)
            .then(() => {
                dispatch(
                    openSnackbar({
                        message: `${datei.name} wurde erfolgreich hochgeladen.`,
                        severity: "success",
                        duration: 6000,
                    })
                );
                dispatch(getAnhaenge(stoerung));
            }
            )
            .catch((error) => {
                logError(error);
                errorHandler(error, dispatch);
            })
    }).finally(() => dispatch(setAnhanglisteLoading(false)));
};

export const getAnhang = (anhang: anhang) => (dispatch: AppDispatch): void => {
    dispatch(setAnhanglisteLoading(true));
    getWithAuth("STOERUNGSANHANGID", {}, anhang.id.toString()).then((response) => {
        const link = document.createElement('a');
        //eslint-disable-next-line scanjs-rules/assign_to_href
        link.href = response;
        document.body.appendChild(link);
        link.click();
    }).catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
    }).finally(() => dispatch(setAnhanglisteLoading(false)));
}

export const deleteAnhang = (anhang: anhang) => (dispatch: AppDispatch): void => {
    dispatch(setAnhanglisteLoading(true));
    deleteWithAuth("STOERUNGSANHANGID", {}, anhang.id.toString()).then(() => {
        dispatch(removeAnhangFromAktuellerStoerung(anhang));
        dispatch(
            openSnackbar({
                message: `${anhang.name} wurde erfolgreich gelöscht.`,
                severity: "success",
                duration: 6000,
            })
        );
    }).catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
    }).finally(() => dispatch(setAnhanglisteLoading(false)));
}

export const exportStoerschriebeToExcel = (id: number, uw: string, messstelle: string, start: Date, ende: Date) => (
    dispatch: AppDispatch
): void => {
    dispatch(setExportLoading(id));
    const promisecreca = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "creca", from: start.toISOString(), to: ende.toISOString(), }, messstelle);
    const promisecrecb = postWithAuth("MESSWERTLOG", { hersteller: "Eberle", datenklasse: "crecb", from: start.toISOString(), to: ende.toISOString(), }, messstelle);
    const promisedatenfelderreca = getWithAuth("DATENFELDER", { hersteller: "Eberle", klasse: "creca", messstelle: messstelle });
    const promisedatenfelderrecb = getWithAuth("DATENFELDER", { hersteller: "Eberle", klasse: "crecb", messstelle: messstelle });
    Promise.all([promisecreca, promisecrecb, promisedatenfelderreca, promisedatenfelderrecb]).then(([stoerschriebecreca, stoerschriebecrecb, datenfelderreca, datenfelderrecb]: [Array<messwertLog>, Array<messwertLog>, Array<messgroesse>, Array<messgroesse>]) => {
        if (stoerschriebecreca.length == 0 && stoerschriebecrecb.length == 0) {
            dispatch(setExportLoading(-1));
            dispatch(
                openSnackbar({
                    message: `Dieser Störung sind keine Störschriebe zugeordnet.`,
                    severity: "info",
                    duration: 6000,
                })
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const promises: Promise<any>[] = [];
            stoerschriebecreca.forEach(item => {
                const startDate = nanosecondsTimeStampToDate(item.start).toISOString();
                const endDate = nanosecondsTimeStampToDate(item.ende).toISOString();
                promises.push(postWithAuth(
                    "MESSWERT",
                    {
                        from: startDate,
                        to: endDate,
                        messstelle: item.messstelle,
                        hersteller: "Eberle",
                        klasse: "creca",
                    },
                    datenfelderreca.map(x => x.feldname).join(",")
                ));
            });
            stoerschriebecrecb.forEach(item => {
                const startDate = nanosecondsTimeStampToDate(item.start).toISOString();
                const endDate = nanosecondsTimeStampToDate(item.ende).toISOString();
                promises.push(postWithAuth(
                    "MESSWERT",
                    {
                        from: startDate,
                        to: endDate,
                        messstelle: item.messstelle,
                        hersteller: "Eberle",
                        klasse: "crecb",
                    },
                    datenfelderrecb.map(x => x.feldname).join(",")
                ));
            });
            Promise.all(promises)
                .then((responses) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const sheets: { fields: netzwirtschaftFieldsData, data: any[] }[] = [];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    responses.forEach((response: any[]) => {
                        const rec: recorder = {
                            klasse: "",
                            kategorie: "",
                            hersteller: "",
                            beschreibung: "",
                        };

                        const fields: netzwirtschaftFieldsData = {
                            uw: "",
                            messpunkt: "",
                            rekorder: rec,
                            messgroesse: Array<messgroesse>(),
                            ersteDaten: new Date(0),
                            letzteDaten: new Date(0),
                            startDatum: new Date(new Date().getTime() - 60 * 60000),
                            endDatum: new Date(),
                        };
                        fields.startDatum = nanosecondsTimeStampToDate(response[0].zeit);
                        fields.endDatum = nanosecondsTimeStampToDate(response[response.length - 1].zeit);
                        fields.uw = uw;
                        fields.messpunkt = messstelle;
                        fields.rekorder.beschreibung = Object.keys(response[0]).includes("oscu0") ? "Oszilloskop (Störschriebe)" : "Effektivwerte (Störschriebe)"
                        fields.messgroesse = Object.keys(response[0]).includes("oscu0") ? datenfelderreca : datenfelderrecb;
                        sheets.push({ fields: fields, data: response });
                    });
                    ExportToExcel(sheets, "export");
                })
                .catch((error) => {
                    logError(error);
                }).finally(() => dispatch(setExportLoading(-1)));
        }
    });
};

export default stoerungsmanagementSlice.reducer